import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { List } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatNumber } from '../lib/format';
import { ApiError, type MailingList } from '../types/api';

export function apiErrorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 403) {
    return 'Only administrators can change this.';
  }
  if (error instanceof ApiError && error.body.message) return error.body.message;
  return fallback;
}

/**
 * Settings → Lists. Admins create, rename and archive lists; a list a
 * campaign has used is archived instead of deleted (docs/api-ui.md "Lister").
 */
export function ListerPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [doubleOptin, setDoubleOptin] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lists', { showArchived }],
    queryFn: () =>
      apiFetch<{ items: MailingList[] }>(`/lists${showArchived ? '?includeArchived=1' : ''}`),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['lists'] });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<MailingList>('/lists', {
        method: 'POST',
        body: { name, description: description || null, doubleOptin, isPublic },
      }),
    onSuccess: (list) => {
      setName('');
      setDescription('');
      setIsPublic(false);
      setNotice(`The list "${list.name}" has been created.`);
      invalidate();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<MailingList>(`/lists/${id}`, { method: 'PATCH', body: { archived } }),
    onSuccess: (list) => {
      setNotice(
        list.archived ? `"${list.name}" has been archived.` : `"${list.name}" has been restored.`,
      );
      invalidate();
    },
  });

  const publicMutation = useMutation({
    mutationFn: ({ id, isPublic: nextPublic }: { id: string; isPublic: boolean }) =>
      apiFetch<MailingList>(`/lists/${id}`, { method: 'PATCH', body: { isPublic: nextPublic } }),
    onSuccess: (list) => {
      setNotice(
        list.isPublic
          ? `"${list.name}" can now be selected in the public signup form.`
          : `"${list.name}" is no longer public.`,
      );
      invalidate();
    },
  });

  const defaultSignupMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<MailingList>(`/lists/${id}/default-signup`, { method: 'PATCH', body: {} }),
    onSuccess: (list) => {
      setNotice(`"${list.name}" is now the default list for signups without a chosen list.`);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (list: MailingList) =>
      apiFetch<{ result: 'deleted' | 'archived' }>(`/lists/${list.id}`, { method: 'DELETE' }),
    onSuccess: (res, list) => {
      setNotice(
        res.result === 'deleted'
          ? `"${list.name}" has been deleted.`
          : `"${list.name}" is used by a campaign and has been archived instead.`,
      );
      invalidate();
    },
  });

  const createError = apiErrorMessage(createMutation.error, 'The list could not be created.');
  const rowError =
    apiErrorMessage(archiveMutation.error, 'The list could not be changed.') ??
    apiErrorMessage(deleteMutation.error, 'The list could not be deleted.') ??
    apiErrorMessage(publicMutation.error, 'The list could not be changed.') ??
    apiErrorMessage(defaultSignupMutation.error, 'The default list could not be changed.');
  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Lists"
        badge={<MockBadge />}
        description="A campaign is sent to the confirmed members of the lists you choose. A list that a campaign has used is archived instead of deleted, so the history is preserved."
      />

      {!isAdmin ? (
        <Alert>You have read-only access. Contact an administrator to change the lists.</Alert>
      ) : (
        <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
          <h2 className="text-sm font-semibold m-0">New list</h2>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="list-name">Name</Label>
              <Input
                id="list-name"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="list-description">Description (optional)</Label>
              <Input
                id="list-description"
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={doubleOptin}
                onChange={(e) => setDoubleOptin(e.target.checked)}
              />
              Require email confirmation (double opt-in)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Public (can be selected in the public signup form)
            </label>
            {createError ? <Alert>{createError}</Alert> : null}
            <Button
              type="submit"
              className="self-end"
              disabled={name.trim() === '' || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create list'}
            </Button>
          </form>
        </Card>
      )}

      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}
      {rowError ? <Alert>{rowError}</Alert> : null}

      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived lists
        </label>
      </div>

      {isLoading ? <div className="text-muted">Loading lists…</div> : null}
      {isError ? <Alert>The lists could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <EmptyState
          icon={<List />}
          title="No lists yet"
          description="A list holds the contacts a campaign can be sent to. Contacts join through signup forms or an import."
          secondaryAction={
            isAdmin ? (
              <a href="#list-name" className="text-sm font-medium">
                Name your first list above
              </a>
            ) : undefined
          }
        />
      ) : null}

      {items.length > 0 ? (
        <div className="relative overflow-x-auto min-w-0 max-w-full">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th scope="col" className="py-2 font-normal">
                  Name
                </th>
                <th scope="col" className="py-2 font-normal text-right">
                  Public
                </th>
                <th scope="col" className="py-2 font-normal text-right">
                  Confirmed
                </th>
                <th scope="col" className="py-2 font-normal text-right">
                  Campaigns
                </th>
                <th scope="col" className="py-2 font-normal text-right">
                  Created
                </th>
                {isAdmin ? (
                  <th scope="col" className="py-2 font-normal text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((list) => (
                <tr key={list.id} className="border-t border-border-soft">
                  <td className="py-2.5">
                    <div className="font-medium">
                      {list.name}
                      {list.archived ? (
                        <span className="ml-2 text-xs text-muted">(archived)</span>
                      ) : null}
                    </div>
                    {list.description ? (
                      <div className="text-xs text-muted">{list.description}</div>
                    ) : null}
                    {list.isDefaultSignup ? (
                      <div className="text-xs text-muted">Default list for signup</div>
                    ) : null}
                  </td>
                  <td className="py-2.5 text-right">
                    {isAdmin ? (
                      <label className="inline-flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={list.isPublic}
                          aria-label={`Public: ${list.name}`}
                          disabled={publicMutation.isPending}
                          onChange={(e) =>
                            publicMutation.mutate({ id: list.id, isPublic: e.target.checked })
                          }
                        />
                      </label>
                    ) : list.isPublic ? (
                      'Yes'
                    ) : (
                      'No'
                    )}
                  </td>
                  <td className="py-2.5 text-right">{formatNumber(list.confirmedCount)}</td>
                  <td className="py-2.5 text-right">{formatNumber(list.campaignCount)}</td>
                  <td className="py-2.5 text-right text-muted">{formatDate(list.createdAt)}</td>
                  {isAdmin ? (
                    <td className="py-2.5 text-right">
                      <div className="flex gap-2 justify-end">
                        {!list.archived && !list.isDefaultSignup ? (
                          <Button
                            variant="secondary"
                            aria-label={`Make ${list.name} the default list`}
                            disabled={defaultSignupMutation.isPending}
                            onClick={() => defaultSignupMutation.mutate(list.id)}
                          >
                            Make default
                          </Button>
                        ) : null}
                        <Button
                          variant="secondary"
                          aria-label={`${list.archived ? 'Restore' : 'Archive'} ${list.name}`}
                          disabled={archiveMutation.isPending}
                          onClick={() =>
                            archiveMutation.mutate({ id: list.id, archived: !list.archived })
                          }
                        >
                          {list.archived ? 'Restore' : 'Archive'}
                        </Button>
                        <Button
                          variant="secondary"
                          aria-label={`Delete ${list.name}`}
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(list)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
